import type { BBox } from '../api'
import { formatDayTime, formatDay, formatNumber } from '../i18n/format'
import { getLang } from '../i18n/store'
import { t } from '../i18n/store'
import type { SeriesFrame } from './seriesCatalog'
import type { ToolsView } from './seriesOverride'
import type { FrameReadiness } from '../models/seriesReadiness'

/**
 * `parametry.txt` — то, ради чего серия отдаётся архивом, а не пятью картинками.
 *
 * Набор PNG без параметров нельзя ни повторить, ни проверить, ни сослаться на
 * него в отчёте: изотерма 9,5 °C и порог фронта 6 °C/100 км по картинке не
 * читаются. Здесь чистая часть — сборка текста; кто и когда её зовёт, знает
 * `exportSeries`.
 *
 * Строки про инструменты берутся из ТОЙ ЖЕ `legendRows`, что рисует плашку на
 * картинке и панель на экране. Написав их заново, мы завели бы третий источник:
 * завтра плашка сказала бы «в кадре нет», а файл промолчал.
 */

export interface ReportHeader {
  /** Бренд ДОМЕНА: архив уходит наружу и обязан назвать того, чей он. */
  brandName: string
  caption?: string
  speciesLabel: string | null
  speciesId: number | null
  /** Дата карты. */
  date: string
  /** Сегодняшняя дата кабинета — чтобы сказать, на сколько суток вперёд кадр. */
  today: string
  center: { lng: number; lat: number }
  zoom: number
  bbox: BBox
  frameSize: { width: number; height: number }
  /** Код палитры домена; null — базовая. */
  palette: string | null
  /** Судьба файла данных: уехал, не собрался или его не просили. */
  dataset?: 'saved' | 'failed' | 'off'
  generatedAt: Date
}

export interface ReportFrame {
  index: number
  total: number
  /** Имя PNG внутри архива. */
  file: string
  frame: SeriesFrame
  /** Значения, при которых кадр снят. */
  tools: ToolsView
  /** Те же строки, что напечатаны на плашке кадра. Тип структурный: сюда
   *  одинаково ложатся и строки легенды экрана, и готовые строки плашки —
   *  важно, что источник ОДИН и файл не спорит с картинкой. */
  legend: { text: string; note?: string }[]
  readiness: FrameReadiness
  waitedMs: number
  /** Инструменты не успели построиться за отведённое время. */
  toolsTimedOut: boolean
  /** Плитки слоя не успели лечь на карту — кадр снят недорисованным. */
  tilesTimedOut?: boolean
}

/** Кадр, которого в архиве нет, и почему. */
export interface ExcludedFrame {
  frame: SeriesFrame
  reason: 'no-data' | 'unknown' | 'skipped' | 'render-failed' | 'tiles-late'
}

const EXCLUDE_KEY: Record<ExcludedFrame['reason'], string> = {
  'no-data': 'map.export.report.excludeNoData',
  unknown: 'map.export.report.excludeUnknown',
  skipped: 'map.export.report.excludeSkipped',
  'render-failed': 'map.export.report.excludeRenderFailed',
  'tiles-late': 'map.export.report.excludeTilesLate',
}

const READINESS_KEY: Record<FrameReadiness, string> = {
  'tiles-ready': 'map.export.report.dataReady',
  rendered: 'map.export.report.dataRendered',
}

const num = (v: number, digits = 1) => formatNumber(getLang(), v, digits)

/** Разница дат в сутках — по календарю, без часовых поясов. */
function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) return 0
  return Math.round((b - a) / 86_400_000)
}

function headerLines(h: ReportHeader): string[] {
  const lines = [t('map.export.report.title'), '']
  lines.push(h.caption ? `${h.brandName} · ${h.caption}` : h.brandName)
  lines.push(t('map.export.report.generated', {
    when: formatDayTime(getLang(), h.generatedAt.toISOString()),
  }))
  lines.push(
    h.speciesLabel && h.speciesId != null
      ? t('map.export.report.species', { name: h.speciesLabel, id: h.speciesId })
      : t('map.export.report.speciesNone'),
  )
  lines.push(t('map.export.report.date', { date: formatDay(getLang(), h.date) }))
  const lead = daysBetween(h.today, h.date)
  lines.push(
    lead > 0 ? t('map.export.report.lead', { days: lead })
      : lead < 0 ? t('map.export.report.past', { days: -lead })
        : t('map.export.report.todayDate'),
  )
  lines.push(t('map.export.report.center', {
    lng: num(h.center.lng, 3), lat: num(h.center.lat, 3), zoom: num(h.zoom, 1),
  }))
  lines.push(t('map.export.report.bbox', {
    west: num(h.bbox.lon_min, 3), east: num(h.bbox.lon_max, 3),
    south: num(h.bbox.lat_min, 3), north: num(h.bbox.lat_max, 3),
  }))
  lines.push(t('map.export.report.size', {
    width: h.frameSize.width, height: h.frameSize.height,
  }))
  lines.push(t('map.export.report.palette', {
    name: h.palette ?? t('map.export.report.paletteBase'),
  }))
  // Про данные кадра молчать нельзя ни в одном исходе: молчание читается как
  // «их не просили», и человек не узнает, что кадр оказался шире потолка.
  if (h.dataset === 'saved') lines.push(t('map.export.report.dataFile'))
  if (h.dataset === 'failed') lines.push(t('map.export.report.dataFileMissing'))
  return lines
}

/**
 * Инструменты кадра. Шаг изолиний волнения и траектории HSI легенда не
 * называет (на карте они узнаются иначе), а в файле параметров молчать о них
 * нельзя: без них кадр не повторить.
 */
function toolLines(f: ReportFrame): string[] {
  const rows = f.legend.map(r => `  · ${[r.text, r.note].filter(Boolean).join(' ')}`)
  if (f.tools.waveOverlay) {
    rows.push(`  · ${t('map.export.report.waveStep', { step: num(f.tools.waveStep) })}`)
  }
  if (f.tools.hsiDrift) rows.push(`  · ${t('map.export.report.hsiDrift')}`)
  return rows.length ? [t('map.export.report.tools'), ...rows] : [t('map.export.report.toolsNone')]
}

function frameLines(f: ReportFrame): string[] {
  const lines = ['', `--- ${t('map.export.report.frameHead', { index: f.index, total: f.total })} ---`]
  lines.push(t('map.export.report.file', { file: f.file }))
  // Кадр ОВЕРЛЕЯ называет и оверлей, и подложку: строка «Слой: Лёд (id
  // bathymetry)» была бы враньём в скобках, а без подложки нельзя понять, на
  // чём именно лежит показанный оверлей.
  if (f.frame.kind === 'overlay') {
    lines.push(t('map.export.report.overlay', {
      name: t(f.frame.labelKey),
      id: f.frame.overlays.join(', '),
      base: t(`layers.${f.frame.layer}`),
    }))
  } else {
    lines.push(t('map.export.report.layer', { name: t(f.frame.labelKey), id: f.frame.layer }))
  }
  // Слою без глубины пишем это словами: «0 м» читалось бы как поверхность.
  lines.push(
    f.tools.depthApplicable
      ? t('map.export.depth', { depth: Math.round(f.tools.depth) })
      : t('map.export.noDepth'),
  )
  lines.push(...toolLines(f))
  if (f.toolsTimedOut) lines.push(t('map.export.report.toolsLate'))
  // Отдельной строкой от инструментов: это РАЗНЫЕ пропажи. Опоздавший
  // инструмент — нет линии поверх поля; опоздавший слой — нет самого поля,
  // и картинка при этом выглядит законной.
  if (f.tilesTimedOut) lines.push(t('map.export.report.tilesLate'))
  lines.push(t(READINESS_KEY[f.readiness]))
  lines.push(t('map.export.report.waited', { sec: num(f.waitedMs / 1000) }))
  return lines
}

/**
 * Кадры, которых в архиве нет.
 *
 * Молчание о пропаже читается как «серия такая и есть»: человек, открывший
 * архив из четырёх картинок, не отличит неполный набор от полного, если ему
 * об этом не сказать. Поэтому пропажа называется поимённо и с причиной —
 * данных нет, тайлы не успели, или это был его собственный выбор.
 */
function excludedLines(excluded: ExcludedFrame[]): string[] {
  if (excluded.length === 0) return []
  return ['', t('map.export.report.excluded'), ...excluded.map(
    e => `  · ${t(e.frame.labelKey)} — ${t(EXCLUDE_KEY[e.reason])}`,
  )]
}

/** Весь файл параметров одним текстом (переносы `\n`; на диск — через `seriesReportFile`). */
export function seriesReportText(
  header: ReportHeader,
  frames: ReportFrame[],
  excluded: ExcludedFrame[] = [],
): string {
  return [
    ...headerLines(header),
    t('map.export.report.framesOf', {
      count: frames.length, total: frames.length + excluded.length,
    }),
    ...excludedLines(excluded),
    ...frames.flatMap(frameLines),
    '',
  ].join('\n')
}

/**
 * Байты файла: UTF-8 с BOM и переносы CRLF.
 *
 * Архив открывают в Windows, а «Блокнот» без BOM читает UTF-8 крокозябрами —
 * человек получил бы файл параметров, который невозможно прочесть.
 */
export function seriesReportFile(text: string): Uint8Array<ArrayBuffer> {
  const crlf = text.replace(/\r\n/g, '\n').replace(/\n/g, '\r\n')
  const body = new TextEncoder().encode(crlf)
  const out = new Uint8Array(body.length + 3)
  out.set([0xef, 0xbb, 0xbf])
  out.set(body, 3)
  return out
}
